import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Skeleton, Stack } from "@mui/material";
import SectionWrapper from "../../Widgets/SectionWrapper";
import EditFields from "./EditFields";
import ViewFields from "./ViewFields";
import { DischargePlanningFormValues } from "./interface";
import useGetDischargePlanning from "hooks/api/client/useGetDischargePlanning";
import useSaveDischargePlanning from "hooks/api/client/useSaveDischargePlanning";


const EMPTY_VALUES: DischargePlanningFormValues = {
  isFadingServices: "",
  fadingPlan: "",
};

const boolToStr = (val: boolean | null | undefined): string => {
  if (val === true) return "YES";
  if (val === false) return "NO";
  return "";
};

const strToBool = (val: string): boolean | null => {
  if (val === "YES") return true;
  if (val === "NO") return false;
  return null;
};

export const DischargePlanning: React.FC = () => {
  const { id: clientId } = useParams<{ id: string }>();
  const clientIdNum = clientId ? Number(clientId) : null;

  const { dischargePlanning, refetchDischargePlanning, isFetched: isDischargePlanningFetched } =
    useGetDischargePlanning(clientIdNum);

  const { saveDischargePlanningMutation, isSaveDischargePlanningLoading } = useSaveDischargePlanning({
    onSuccess: () => {
      refetchDischargePlanning();
      setEditMode(false);
    },
  });

  const [editMode, setEditMode] = useState(false);
  const [formValues, setFormValues] = useState<DischargePlanningFormValues>(EMPTY_VALUES);
  const [initialValues, setInitialValues] = useState<DischargePlanningFormValues>(EMPTY_VALUES);

  useEffect(() => {
    if (dischargePlanning) {
      const values: DischargePlanningFormValues = {
        isFadingServices: boolToStr(dischargePlanning.is_fading_services),
        fadingPlan: dischargePlanning.fading_plan || "",
      };
      setFormValues(values);
      setInitialValues(values);
    }
  }, [dischargePlanning]);

  const handleChange =
    (field: keyof DischargePlanningFormValues) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const nextValue = event.target.value;
      setFormValues((prev) => {
        if (field === "isFadingServices" && nextValue !== "YES") {
          return {
            ...prev,
            isFadingServices: nextValue,
            fadingPlan: "",
          };
        }

        return { ...prev, [field]: nextValue };
      });
    };

  const handleSave = () => {
    if (!clientIdNum) return;
    const shouldSendFadingPlan = formValues.isFadingServices === "YES";
    const payload = {
      is_fading_services: strToBool(formValues.isFadingServices),
      fading_plan: shouldSendFadingPlan ? formValues.fadingPlan || null : null,
    };
    saveDischargePlanningMutation({
      clientId: clientIdNum,
      recordId: dischargePlanning?.an_internal_id,
      payload,
    });
  };

  const handleCancel = () => {
    setFormValues(initialValues);
    setEditMode(false);
  };

  const saveDisabled =
    JSON.stringify(formValues) === JSON.stringify(initialValues) ||
    isSaveDischargePlanningLoading;

  if (!isDischargePlanningFetched) {
    return (
      <SectionWrapper
        title="Transition & Discharge Planning"
        description="Criteria for transitioning clients out of ABA services"
        hideButtons
      >
        <Stack gap={2}>
          <Skeleton variant="text" width="30%" height={28} />
          <Skeleton variant="rounded" height={56} />
          <Skeleton variant="text" width="30%" height={28} />
          <Skeleton variant="rounded" height={56} />
        </Stack>
      </SectionWrapper>
    );
  }

  return (
    <SectionWrapper
      title="Transition & Discharge Planning"
      description="Criteria for transitioning clients out of ABA services"
      editMode={editMode}
      onEdit={() => setEditMode(true)}
      onCancel={handleCancel}
      onSave={handleSave}
      saveDisabled={saveDisabled}
    >
      {editMode ? (
        <EditFields formValues={formValues} handleChange={handleChange} />
      ) : (
        <ViewFields formValues={formValues} />
      )}
    </SectionWrapper>
  );
};

export default DischargePlanning;
